import React, { useState } from "react"
import ReactHtmlParser from "react-html-parser"
import useWindowWidth from "../../hooks/useWindowWidth"
import * as SelectionFactoryHelper from "../helpers/SelectionFactoryHelper"
import PropTypes, { arrayOf } from "prop-types"
import { object } from "underscore"

export default function ParkingProductPricingTableContainer({ headline, subHeadline, parkingProductTableBlocks }) {
  const [activeColumnIndex, setActiveColumnIndex] = useState(0) // default to the first column after row headers
  const windowWidth = useWindowWidth() // Using your custom hook

  return (
    <section className={"product-pricing-table-container w--wider"}>
      <h2 id={headline ? headline.replaceAll(" ", "-") : ""} className="parking-product-pricing-header">
        {headline}
      </h2>
      <div className="subheadline">{ReactHtmlParser(subHeadline)}</div>

      {windowWidth < 950 && (
        <div className="tabs">
          {parkingProductTableBlocks[0]?.parkingPricingColumns
            .filter((column) => column.__type__ === "ParkingProductPricingColumnBlock")
            .map((column, index) => (
              <button key={index} className={`tab ${activeColumnIndex === index ? "active" : ""}`} onClick={() => setActiveColumnIndex(index)}>
                {column.columnHeader}
              </button>
            ))}
        </div>
      )}

      {parkingProductTableBlocks && parkingProductTableBlocks.length > 0
        ? parkingProductTableBlocks.map((table, index) => {
            return (
              <ParkingProductPricingTablehead
                key={table.tableHeading + "-" + (table?.productCodes[0]?.carparkCode ?? "code-less" + "-" + index)}
                tableHeading={table.tableHeading}
                productCodes={table.productCodes}
                tableNotice={table.tableNotice}
                parkingPricingColumns={table.parkingPricingColumns}
                activeColumnIndex={activeColumnIndex}
                windowWidth={windowWidth}
              />
            )
          })
        : undefined}
    </section>
  )
}

ParkingProductPricingTableContainer.propTypes = {
  headline: PropTypes.string,
  subHeadline: PropTypes.string,
  parkingProductTableBlocks: arrayOf(object),
}

function ParkingProductPricingTablehead({ tableHeading, productCodes, tableNotice, parkingPricingColumns, activeColumnIndex, windowWidth }) {
  const initialColumnBlock = parkingPricingColumns.find((column) => column.__type__ === "ParkingProductPricingTableInitialColumnBlock")
  const otherColumns = parkingPricingColumns.filter((column) => column.__type__ === "ParkingProductPricingColumnBlock")

  const activeColumn = windowWidth < 950 && activeColumnIndex !== null ? otherColumns[activeColumnIndex] : null

  const numOfDataColumns = otherColumns && otherColumns.length

  const columnClass =
    numOfDataColumns == 1 ? "single" : numOfDataColumns == 2 ? "double" : numOfDataColumns == 3 ? "triple" : numOfDataColumns == 4 ? "quad" : ""

  return (
    <table className="product-pricing-table">
      <caption className="product-pricing-table-caption">
        {tableHeading}
        <div className="product-codes">
          {productCodes && productCodes.length > 0
            ? productCodes.map((code, index) => (
                <div key={index} className="code-container">
                  <span className={`code ${SelectionFactoryHelper.getFromSelectionFactory(code.style)}`}>{code.carparkCode}</span>
                  {code.carparkCodeDescriptor && <p>{code.carparkCodeDescriptor}</p>}
                </div>
              ))
            : undefined}
        </div>
        {tableNotice && <p className="notice">{tableNotice}</p>}
      </caption>

      <thead className={windowWidth < 950 ? "hidden-header" : ""}>
        <tr>
          {/* Top left cell is always empty. */}
          <th className={columnClass} aria-label="start"></th>
          {otherColumns.map((column, index) => (
            <th className={columnClass} key={index} aria-hidden={windowWidth < 950 && activeColumnIndex !== index}>
              <p>{column.columnHeader}</p>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {windowWidth < 950 && activeColumn?.isEmpty ? (
          <tr className="empty-row">
            <td className="empty-cell" colSpan={initialColumnBlock?.rowHeads.length + 1}>
              <p className="empty-description">{activeColumn.isEmptyDescription}</p>
            </td>
          </tr>
        ) : (
          initialColumnBlock?.rowHeads.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <th className={columnClass} scope="row">
                <p>
                  {row.rowHead}
                  {row.subDescription && <small>{row.subDescription}</small>}
                </p>
              </th>

              {otherColumns.map((column, colIndex) => {
                if (windowWidth < 950 && colIndex !== activeColumnIndex) return null

                return (
                  <td className={columnClass} key={colIndex}>
                    {windowWidth < 950 && column.isEmpty ? (
                      <p>
                        <span className="empty-description">{column.isEmptyDescription}</span>
                      </p>
                    ) : column?.rowData?.length > 0 ? (
                      <p>
                        <span>{column.rowData[rowIndex]}</span>
                      </p>
                    ) : (
                      <span className="empty-cell"></span>
                    )}
                  </td>
                )
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

ParkingProductPricingTablehead.propTypes = {
  tableHeading: PropTypes.string,
  productCodes: PropTypes.arrayOf(object),
  tableNotice: PropTypes.string,
  parkingPricingColumns: arrayOf(object),
  activeColumnIndex: PropTypes.number,
  windowWidth: PropTypes.number,
}
